import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import axios from 'axios';
import API_CONFIG from '../config/api';

gsap.registerPlugin(ScrollTrigger);

const Gallery = () => {
  const galleryRef = useRef(null);
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get('/api/gallery/grouped');
      // console.log('Gallery API response:', response.data);
      
      // Process grouped events from API
      const grouped = response.data.map(event => {
        // Get the first photo to determine event date
        const firstPhoto = event.photos[0];
        const eventDate = firstPhoto?.eventDate || firstPhoto?.createdAt;
        
        return {
          eventName: event.eventName,
          eventData: {
            _id: event.eventName,
            eventName: event.eventName,
            startDate: eventDate, // Use eventDate from first photo
            endDate: eventDate
          },
          photos: event.photos
        };
      })
      .sort((a, b) => {
        const dateA = a.eventData.startDate ? new Date(a.eventData.startDate) : new Date(0);
        const dateB = b.eventData.startDate ? new Date(b.eventData.startDate) : new Date(0);
        return dateB - dateA;
      })
      .slice(0, 3); // Only latest 3 events
      
      // console.log('Grouped events:', grouped);
      setEvents(grouped);
    } catch (error) {
      // // console.log('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!loading && events.length > 0 && galleryRef.current) {
      const items = galleryRef.current.querySelectorAll('.gallery-apple-card');
      gsap.fromTo(
        items,
        { opacity: 0, y: 60, scale: 0.92 },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 1.1,
          ease: 'power3.out',
          stagger: 0.15,
          scrollTrigger: {
            trigger: galleryRef.current,
            start: 'top 80%',
            once: true
          }
        }
      );
      return () => ScrollTrigger.getAll().forEach(t => t.kill());
    }
  }, [loading, events]);

  const handleViewMore = () => {
    navigate('/gallery');
  };

  const handleEventClick = (event) => {
    setSelectedEvent(event);
  };

  const handleImageClick = (photo, photoIndex) => {
    setSelectedImage(photo);
    setCurrentImageIndex(photoIndex);
  };

  const handleCloseImage = () => {
    setSelectedImage(null);
    setCurrentImageIndex(0);
  };

  const handleNextImage = () => {
    if (selectedEvent && selectedEvent.photos) {
      const nextIndex = (currentImageIndex + 1) % selectedEvent.photos.length;
      setCurrentImageIndex(nextIndex);
      setSelectedImage(selectedEvent.photos[nextIndex]);
    }
  };

  const handlePrevImage = () => {
    if (selectedEvent && selectedEvent.photos) {
      const prevIndex = currentImageIndex === 0 ? selectedEvent.photos.length - 1 : currentImageIndex - 1;
      setCurrentImageIndex(prevIndex);
      setSelectedImage(selectedEvent.photos[prevIndex]);
    }
  };

  if (loading) {
    return (
      <div className="py-6 sm:py-8 md:py-10 bg-[#f5f5f7] dark:bg-[#18181a]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">
              Tournament <span className="text-orange-600">Highlights</span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-orange-500 to-red-600 mx-auto mb-4"></div>
            <p className="section-subtitle max-w-3xl mx-auto">
              Capturing the best moments from our cricket tournaments and events
            </p>
          </div>
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        </div>
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="py-6 sm:py-8 md:py-10 bg-[#f5f5f7] dark:bg-[#18181a]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">
              Tournament <span className="text-orange-600">Highlights</span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-orange-500 to-red-600 mx-auto mb-4"></div>
            <p className="section-subtitle max-w-3xl mx-auto">
              Capturing the best moments from our cricket tournaments and events
            </p>
          </div>
          <div className="text-center py-8 text-gray-500">
            <p>No events available yet</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-16 px-2 bg-[#f5f5f7] dark:bg-[#18181a] border border-gray-200 rounded-lg">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">
            Tournament <span className="text-orange-600">Highlights</span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-orange-500 to-red-600 mx-auto mb-4"></div>
          <p className="section-subtitle max-w-3xl mx-auto">
            Capturing the best moments from our cricket tournaments and events
          </p>
        </div>

        <div
          ref={galleryRef}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 px-4 sm:px-6 lg:px-8"
        >
          {events.map((event) => {
            const representativePhoto = event.photos[0];
            return (
              <div
                key={event.eventData._id}
                className="bg-white border border-gray-200 rounded-lg overflow-hidden
                         transform transition-all duration-300 hover:scale-105
                         hover:shadow-lg shadow-md h-auto min-h-[400px] flex flex-col"
                tabIndex={0}
                onClick={() => handleEventClick(event)}
              >
                <div className="relative">
                  <img
                    src={representativePhoto.image}
                    alt={event.eventName}
                    loading="lazy"
                    className="w-full h-[200px] sm:h-[250px] md:h-[280px] object-cover flex-shrink-0"
                    onError={e => { 
                      // Replace with alternative image instead of hiding
                      e.target.src = '/images/heroSection_img.jpg';
                    }}
                  />
                </div>
                
                {/* Event Details Below Photo */}
                <div className="p-4 flex-grow flex flex-col">
                  <h3 className="text-lg font-semibold mb-2 text-gray-900 line-clamp-2">
                    {event.eventName}
                  </h3>
                  <div className="flex items-center justify-between text-xs text-blue-600 mb-2">
                    <span className="font-medium">
                      {new Date(event.eventData.startDate).toLocaleDateString('en-IN', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </span>
                    <span className="text-gray-500">
                      {event.photos.length} Photos
                    </span>
                  </div>
                  <div className="mt-auto space-y-2">
                    <p className="text-gray-700 text-xs">
                      <span className="font-medium">📍 Location:</span> 118- Ward No.2, Kabir Nagar, Raipur
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        
        {/* See More Button */}
        <div className="text-center mt-8">
          <button
            onClick={handleViewMore}
            className="text-blue-600 hover:text-blue-700 font-medium text-sm sm:text-base flex items-center gap-1 mx-auto"
          >
            See More
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>

      {/* Event Photos Modal */}
      {selectedEvent && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 backdrop-blur-sm flex items-center justify-center p-4 z-50"
          onClick={() => setSelectedEvent(null)}
        >
          <div className="relative max-w-6xl w-full max-h-[90vh] overflow-hidden bg-white rounded-2xl shadow-2xl" onClick={e => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">{selectedEvent.eventName}</h3>
                </div>
                <button
                  onClick={() => setSelectedEvent(null)}
                  className="text-gray-500 hover:text-gray-700 p-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Photos Grid */}
            <div className="p-6 max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {selectedEvent.photos.map((photo, photoIndex) => (
                  <div
                    key={photo.id}
                    className="relative group cursor-pointer transform transition-all duration-300 hover:scale-105"
                    onClick={() => handleImageClick(photo, photoIndex)}
                  >
                    <div className="relative overflow-hidden rounded-xl shadow-lg bg-white">
                      <img
                        src={photo.image}
                        alt={photo.title || selectedEvent.eventName}
                        loading="lazy"
                        className="w-full h-40 object-cover transition-transform duration-500 group-hover:scale-110"
                        onError={e => {
                          // // console.log('Modal photo failed to load:', e.target.src);
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-6 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">
                  Event Date: {new Date(selectedEvent.eventData.startDate).toLocaleDateString('en-IN', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                </span>
                <button
                  onClick={() => navigate('/gallery')}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                >
                  View Full Gallery
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-95 backdrop-blur-sm flex items-center justify-center p-4 z-[100]">
          <div className="relative max-w-7xl w-full max-h-[90vh] flex items-center justify-center">
            {/* Close Button */}
            <button
              onClick={handleCloseImage}
              className="absolute top-4 right-4 text-white hover:text-gray-300 p-2 rounded-lg hover:bg-white/20 transition-colors z-10"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Previous Button */}
            {selectedEvent && selectedEvent.photos && selectedEvent.photos.length > 1 && (
              <button
                onClick={handlePrevImage}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 p-3 rounded-lg hover:bg-white/20 transition-colors z-10"
              >
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}

            {/* Next Button */}
            {selectedEvent && selectedEvent.photos && selectedEvent.photos.length > 1 && (
              <button
                onClick={handleNextImage}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 p-3 rounded-lg hover:bg-white/20 transition-colors z-10"
              >
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )}

            {/* Image */}
            <img
              src={selectedImage.image}
              alt={selectedImage.title || selectedEvent?.eventName || 'Gallery Image'}
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
              onError={e => {
                e.target.style.display = 'none';
              }}
            />

            {/* Image Counter */}
            {selectedEvent && selectedEvent.photos && selectedEvent.photos.length > 1 && (
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/50 text-white px-4 py-2 rounded-lg text-sm">
                {currentImageIndex + 1} / {selectedEvent.photos.length}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;
